import * as yup from 'yup';
import { error } from '../text';
import { productStatus } from '../../constants/Enum';

const companyValidation = yup
  .number()
  .min(1, error.requiredSelect)
  .required(error.requiredSelect);

const aliasValidation = yup
  .string()
  .required(error.required)
  .max(250, error.below250);

const nameValidation = yup
  .string()
  .required(error.required)
  .max(250, error.below250);

const specificationValidation = yup
  .string()
  .nullable()
  .max(250, error.below250);

const summaryValidation = yup
  .string()
  .required(error.required)
  .max(2000, error.below2000);

const conditionValidation = yup.string().nullable().max(250, error.below250);

const imagesValidation = yup
  .array()
  .of(
    yup.object().shape({
      name: yup
        .string()
        .required(error.required)
        .matches(/.(jpg|jpeg|png|gif|svg|webp)$/i, error.image)
        .max(250, error.fileNameBelow250),
      sort: yup.number().nullable(),
      binary: yup.string().nullable(),
      kind: yup.string().nullable(),
      id: yup.number().nullable(),
      size: yup.number().max(10000000, error.imageSize).nullable()
    })
  )
  .nullable();

const documentValidation = yup
  .object()
  .shape({
    name: yup
      .string()
      .nullable()
      .matches(/.pdf$/i, error.document)
      .max(250, error.fileNameBelow250),
    binary: yup.string().nullable(),
    kind: yup.string().nullable(),
    id: yup.number().nullable(),
    size: yup.number().max(20000000, error.fileSize).nullable()
  })
  .nullable();

const documentDescriptionUrlValidation = yup
  .string()
  .nullable()
  .url(error.url)
  .max(250, error.below250);

const categoryValidation = yup
  .number()
  .min(1, error.requiredSelect)
  .required(error.required);

const materialValidation = yup
  .number()
  .min(1, error.requiredSelect)
  .required(error.required);

const calculationSpecificationValidation = yup.string().nullable();

const emissionIntensityValidation = yup
  .number()
  .nullable()
  .transform(
    (value, originalValue) =>
      (isNaN(Number(originalValue))
        ? originalValue
        : Number(originalValue)) as number
  )
  .test('is-decimal-3-places', error.decimal3, (value) => {
    if (!value) return true;
    if (value >= 100) {
      return Number.isInteger(value);
    } else {
      return /^[0-9]+(\.[0-9]{1,3})?$/.test(value.toString());
    }
  })
  .test(
    'is-number',
    error.number,
    (value) =>
      value === undefined ||
      value === null ||
      (value !== undefined && value !== null && !isNaN(Number(value)))
  )
  .min(0, error.abave1)
  .typeError(error.number);

const reduceRateValidation = yup
  .number()
  .nullable()
  .transform((value, originalValue) =>
    isNaN(Number(originalValue)) ? originalValue : Number(originalValue)
  )
  .test('is-decimal', error.decimal, (value) => {
    if (value === undefined || value === null) return true;
    if (!isNaN(Number(value))) {
      const parts = value.toString().split('.');
      return parts.length < 2 || parts[1].length <= 1;
    }
    return false;
  })
  .min(0, error.abave1)
  .max(99999, error.below100000)
  .typeError(error.number);

const calculationSupplementValidation = yup
  .string()
  .nullable()
  .max(2000, error.below2000);

const calculationBasisTypeValidation = yup
  .number()
  .required(error.requiredSelect);

const calculationBasisNameValidation = yup
  .string()
  .nullable()
  .max(250, error.below250);

const calculationBasisFileValidation = yup
  .object()
  .shape({
    name: yup
      .string()
      .nullable()
      .matches(/.pdf$/i, error.document)
      .max(250, error.fileNameBelow250),
    binary: yup.string().nullable(),
    kind: yup.string().nullable(),
    id: yup.number().nullable(),
    size: yup.number().max(20000000, error.fileSize).nullable()
  })
  .nullable();

const calculationBasisFileUrlValidation = yup
  .string()
  .nullable()
  .url(error.url)
  .max(250, error.below250);

const otherCertificationValidation = yup
  .string()
  .nullable()
  .max(2000, error.below2000);

const priceConditionValidation = yup
  .string()
  .nullable()
  .max(250, error.below250);

const referenceUnitPriceValidation = yup
  .number()
  .nullable()
  .transform(
    (value, originalValue) =>
      (isNaN(Number(originalValue))
        ? originalValue
        : Number(originalValue)) as number
  )
  .integer(error.integer)
  .test(
    'is-number',
    error.number,
    (value) =>
      value === undefined ||
      value === null ||
      (value !== undefined && value !== null && !isNaN(Number(value)))
  )
  .min(0, error.abave1)
  .max(1000000000000000, error.below1000000000000000)
  .typeError(error.number);

const settingAtValidation = yup.date().nullable().typeError(error.date);

const relatedUrlValidation = yup
  .string()
  .nullable()
  .url(error.url)
  .max(250, error.below250);

const remarkValidation = yup.string().nullable().max(2000, error.below2000);

const assigneeValidation = yup
  .string()
  .min(1, error.requiredSelect)
  .required(error.required);

const recommendFlgValidation = yup
  .number()
  .test(
    'is-zero-or-one',
    error.requiredSelect,
    (value) => value === 1 || value === 0
  );

const operatorNameValidation = yup.string().nullable();

const operatorMemoValidation = yup
  .string()
  .nullable()
  .max(2000, error.below2000);

const statusValidation = yup.number().required(error.requiredSelect);

const rejectReasonValidation = yup
  .string()
  .when('status', (values: number[]) => {
    const status = values[0] as number;
    return status === productStatus.reject
      ? yup.string().max(250, error.below250).required(error.required)
      : yup.string().max(250, error.below250).nullable();
  });

const carbonOffsetFlgValidation = yup
  .number()
  .required(error.requiredSelect)
  .test(
    'is-zero-or-one',
    error.requiredSelect,
    (value) => value === 1 || value === 0
  );

export const productSchema = yup.object().shape({
  userId: yup.number().nullable(),
  companyId: yup.number().nullable(),
  alias: aliasValidation,
  name: nameValidation,
  specification: specificationValidation,
  summary: summaryValidation,
  condition: conditionValidation,
  images: imagesValidation,
  document: documentValidation,
  documentDescriptionUrl: documentDescriptionUrlValidation,
  categoryId: categoryValidation,
  materialId: materialValidation,
  calculationSpecification: calculationSpecificationValidation,
  emissionIntensity: emissionIntensityValidation,
  reduceRate: reduceRateValidation,
  calculationSupplement: calculationSupplementValidation,
  calculationBasisType: calculationBasisTypeValidation,
  calculationBasisName: calculationBasisNameValidation,
  calculationBasisFile: calculationBasisFileValidation,
  calculationBasisFileUrl: calculationBasisFileUrlValidation,
  otherCertification: otherCertificationValidation,
  priceCondition: priceConditionValidation,
  referenceUnitPrice: referenceUnitPriceValidation,
  settingAt: settingAtValidation,
  priceRemarks: remarkValidation,
  relatedUrl: relatedUrlValidation,
  remark: remarkValidation,
  assignee: assigneeValidation,
  carbonOffsetFlg: carbonOffsetFlgValidation
});

export const adminProductSchema = yup.object().shape({
  company: companyValidation,
  alias: aliasValidation,
  name: nameValidation,
  specification: specificationValidation,
  summary: summaryValidation,
  condition: conditionValidation,
  images: imagesValidation,
  document: documentValidation,
  documentDescriptionUrl: documentDescriptionUrlValidation,
  categoryId: categoryValidation,
  materialId: materialValidation,
  calculationSpecification: calculationSpecificationValidation,
  emissionIntensity: emissionIntensityValidation,
  reduceRate: reduceRateValidation,
  calculationSupplement: calculationSupplementValidation,
  calculationBasisType: calculationBasisTypeValidation,
  calculationBasisName: calculationBasisNameValidation,
  calculationBasisFile: calculationBasisFileValidation,
  calculationBasisFileUrl: calculationBasisFileUrlValidation,
  otherCertification: otherCertificationValidation,
  priceCondition: priceConditionValidation,
  referenceUnitPrice: referenceUnitPriceValidation,
  settingAt: settingAtValidation,
  priceRemarks: remarkValidation,
  relatedUrl: relatedUrlValidation,
  remark: remarkValidation,
  assignee: assigneeValidation,
  recommendFlg: recommendFlgValidation,
  operatorName: operatorNameValidation,
  operatorMemo: operatorMemoValidation,
  carbonOffsetFlg: carbonOffsetFlgValidation
});

export const adminProductStatusSchema = yup.object().shape({
  status: statusValidation,
  rejectReason: rejectReasonValidation,
  recommendFlg: recommendFlgValidation,
  operatorName: operatorNameValidation,
  operatorMemo: operatorMemoValidation
});

export const productSearchSchema = yup.object().shape({
  categoryId: yup.number().nullable(),
  materialId: yup.number().nullable(),
  freeWord: yup.string().nullable().max(250, error.below250),
  reduceRate: yup.number().nullable(),
  calculationBasisType: yup
    .array()
    .of(yup.boolean().required())
    .required(error.required),
  carbonOffsetFlg: yup
    .array()
    .of(yup.boolean().required())
    .required(error.required),
  certificationFlg: yup.number().nullable(),
  recommendFlg: yup
    .array()
    .of(yup.boolean().required())
    .required(error.required),
  favoriteMaterialsFlg: yup
    .array()
    .of(yup.boolean().required())
    .required(error.required),
  evaluation: yup.number().nullable(),
  status: yup.array().of(yup.number().required()),
  company: yup.string().nullable().max(250, error.below250)
});
